import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useRole } from "@/hooks/useRole";
import { useUserProfile } from "@/hooks/useUserProfile";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";

const NicknameDebug = () => {
  const { authUser } = useRole();
  const { profile, refreshProfile } = useUserProfile();
  const [customNickname, setCustomNickname] = useState("Deployjogador");
  const [debugInfo, setDebugInfo] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const checkCurrentData = async () => {
    if (!authUser) {
      toast({
        title: "Erro",
        description: "Usuário não autenticado",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      const { data: profileData, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", authUser.id)
        .maybeSingle();

      if (error) {
        throw error;
      }

      setDebugInfo(profileData);
      
      toast({
        title: "Dados carregados",
        description: "Verifique as informações abaixo"
      });
    } catch (error: any) {
      toast({
        title: "Erro",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const updateNickname = async (nickname: string) => {
    if (!authUser) {
      toast({
        title: "Erro",
        description: "Usuário não autenticado",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ nickname: nickname })
        .eq('id', authUser.id);

      if (error) {
        throw error;
      }

      toast({
        title: "Sucesso",
        description: `Nickname atualizado para "${nickname}"`,
      });

      // Recarregar dados
      await checkCurrentData();
      refreshProfile();
    } catch (error: any) {
      toast({
        title: "Erro",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-[#00B6F0]">🔧 Debug do Nickname</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {authUser ? (
            <div className="text-green-600">
              ✅ Usuário autenticado: {authUser.email}
            </div>
          ) : (
            <div className="text-red-600">
              ❌ Usuário não autenticado
            </div>
          )}

          <div className="grid gap-4">
            <Button 
              onClick={checkCurrentData} 
              disabled={loading || !authUser}
              className="bg-[#00B6F0] hover:bg-[#0095c7]"
            >
              🔍 Verificar Dados Atuais
            </Button>

            <Button 
              onClick={() => updateNickname("Deployjogador")} 
              disabled={loading || !authUser}
              variant="outline"
            >
              🚀 Corrigir para "Deployjogador"
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Dados do Hook useUserProfile */}
      <Card>
        <CardHeader>
          <CardTitle>📊 Hook useUserProfile</CardTitle>
        </CardHeader>
        <CardContent>
          {profile ? (
            <div className="space-y-2">
              <div>
                <Badge variant="outline">Nickname</Badge>: {profile.nickname}
              </div>
              <div>
                <Badge variant="outline">Role</Badge>: {profile.role}
              </div>
              <div>
                <Badge variant="outline">Imagem</Badge>: {profile.image_url ? "✅ Presente" : "❌ Ausente"}
              </div>
              <div>
                <Badge variant="outline">Jogo</Badge>: {profile.preferred_game || "Não definido"}
              </div>
            </div>
          ) : (
            <div className="text-gray-500">Carregando...</div>
          )}
        </CardContent>
      </Card>

      {/* Dados brutos da tabela */}
      {debugInfo && (
        <Card>
          <CardHeader>
            <CardTitle>🗃️ Dados Brutos da Tabela</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="bg-gray-100 p-4 rounded text-sm overflow-auto">
              {JSON.stringify(debugInfo, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}

      {/* Atualização customizada */}
      <Card>
        <CardHeader>
          <CardTitle>✏️ Atualização Customizada</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input
              value={customNickname}
              onChange={(e) => setCustomNickname(e.target.value)}
              placeholder="Digite o nickname"
            />
            <Button 
              onClick={() => updateNickname(customNickname)}
              disabled={loading || !authUser || !customNickname.trim()}
              variant="outline"
            >
              Atualizar
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default NicknameDebug; 